import { FormEventHandler, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';

import { VehicleInfo } from './_authenticated._main.vehicle/VehicleInfo';
import { TextInput } from '../components/atomic/TextInput';
import { GradientButton } from '../components/button/GradientButton';
import { PrimaryButton } from '../components/button/PrimaryButton';
import { SecondaryButton } from '../components/button/SecondaryButton';
import { useCreateVehicle } from '../hooks/useCreateVehicle';
import { useFetchVehicleInfo } from '../hooks/useFetchVehicleInfo';
import { t } from '../services/translation/translation';
import { formatLicencePlateNumber, getPanelImage, getVehicleName } from '../utils/vehicle';

export const VehicleAddRoute = () => {
  const navigate = useNavigate();
  const [licencePlate, setLicencePlate] = useState('');
  const [errorMessage, setErrorMessage] = useState<string>();
  const { data: vehicle, mutate: searchVehicle, isLoading: isSearching, reset, error } = useFetchVehicleInfo();
  const { mutate: createVehicle, isLoading: isCreatingVehicle } = useCreateVehicle();

  const handleChangeText = (text: string) => {
    reset();
    setLicencePlate(formatLicencePlateNumber(text));
    setErrorMessage(undefined);
  };

  const handleSearch: FormEventHandler<HTMLFormElement> = e => {
    e.preventDefault();
    if (/^[A-Z]{2}-\d{3}-[A-Z]{2}$/.test(licencePlate)) {
      searchVehicle(licencePlate);
    } else {
      setErrorMessage('error');
    }
  };

  const handleCreate = () => {
    if (licencePlate) {
      createVehicle(
        { ...vehicle, licensePlate: licencePlate },
        {
          onSuccess: () => {
            navigate('/vehicles');
          },
        },
      );
    }
  };

  return (
    <div>
      <Helmet>
        <title>{t('vehicle-add-route.title')} - Allocab</title>
      </Helmet>
      <h3 className="text-3xl font-bold dark:text-white mb-6">{t('vehicle-add-route.title')}</h3>
      <div>
        <form className="space-y-2 inline-block" onSubmit={handleSearch}>
          <TextInput
            label={t('common.licence-plate')}
            className="w-full"
            value={licencePlate}
            onChange={e => handleChangeText(e.currentTarget.value)}
            errorMessage={errorMessage}
            maxLength={9}
            placeholder="AB-123-CD"
          />
          <PrimaryButton type="submit" className="w-full" text={t('common.search')} isLoading={isSearching} />
        </form>
      </div>
      {vehicle ? (
        <div className="inline-block p-6 bg-white border border-gray-200 rounded-lg shadow dark:bg-gray-800 dark:border-gray-700 mt-4 space-y-4">
          <h6 className="text-lg font-bold dark:text-white capitalize">{getVehicleName(vehicle).toLowerCase()}</h6>
          <div className="flex gap-4">
            <img className="w-48 mr-2 self-center" src={getPanelImage(vehicle.panelKeys)} alt="Voiture" />
            <VehicleInfo vehicle={vehicle} />
          </div>
          <div className="flex items-center space-x-2">
            <GradientButton onClick={handleCreate} isLoading={isCreatingVehicle}>
              {t('common.add')}
            </GradientButton>
            <SecondaryButton onClick={() => handleChangeText('')}>{t('common.reset')}</SecondaryButton>
          </div>
        </div>
      ) : null}
      {error ? (
        <div>
          <div className="inline-block p-6 max-w-sm bg-white border border-gray-200 rounded-lg shadow dark:bg-gray-800 dark:border-gray-700 mt-4 space-y-4">
            <h6 className="text-lg font-bold dark:text-white capitalize">{t('vehicle-add-route.not-found')}</h6>
            <div className="h-48 w-48 m-auto bg-confused-light dark:bg-confused-dark bg-no-repeat bg-contain bg-center" />
            <p className="text-sm">{t('vehicle-add-route.not-found-description', { licencePlate })}</p>
            <div className="flex items-center space-x-2">
              <GradientButton onClick={handleCreate} isLoading={isCreatingVehicle}>
                {t('common.add')}
              </GradientButton>
              <SecondaryButton onClick={() => handleChangeText('')}>{t('common.reset')}</SecondaryButton>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
